/**
 * The selected definition's two tabs, Definition and Runs, as the WAI-ARIA
 * tabs pattern: only the selected tab is in the Tab order (a roving
 * tabindex), Left/Right move between the tabs and Home/End go to the first
 * and last, each selecting the tab it moves to and focusing it.
 */

import { useRef, type KeyboardEvent, type ReactElement } from 'react';

export type DefinitionTabId = 'definition' | 'runs';

interface DefinitionTabsProps {
  tab: DefinitionTabId;
  /** A proposed definition has no runs yet: the Runs tab says so. */
  proposed: boolean;
  onChange: (tab: DefinitionTabId) => void;
}

export function DefinitionTabs({ tab, proposed, onChange }: DefinitionTabsProps): ReactElement {
  const tabs: [DefinitionTabId, string][] = [
    ['definition', 'Definition'],
    ['runs', proposed ? 'Runs (none, not enabled)' : 'Runs'],
  ];
  const refs = useRef<Partial<Record<DefinitionTabId, HTMLButtonElement | null>>>({});
  const move = (to: number): void => {
    const target = tabs[(to + tabs.length) % tabs.length];
    if (target === undefined) return;
    onChange(target[0]);
    refs.current[target[0]]?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const to = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: tabs.length - 1 }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    move(to);
  };
  return (
    <div className="def-tabs" role="tablist" aria-label="Definition views">
      {tabs.map(([id, label], index) => (
        <button
          key={id}
          ref={(el) => { refs.current[id] = el; }}
          type="button"
          role="tab"
          id={`def-tab-${id}`}
          aria-selected={tab === id}
          aria-controls="def-tabpanel"
          tabIndex={tab === id ? 0 : -1}
          className={`def-tabs__tab${tab === id ? ' def-tabs__tab--on' : ''}${proposed && id === 'runs' ? ' def-tabs__tab--muted' : ''}`}
          onClick={() => { onChange(id); }}
          onKeyDown={(event) => { onKeyDown(event, index); }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
